// Commercial service data — consumed by <ServiceDetail track="commercial" />.
// Hero and material images are photorealistic Higgsfield-generated stock
// photos (frontend/public/images/commercial/), one per service/material.

export const COMMERCIAL_SERVICES_DATA = {
    "commercial-fencing-gates": {
        slug: "commercial-fencing-gates",
        category: "Commercial · Fencing & Gates",
        name: "Commercial Fencing & Gates",
        heroTitle: "Secure perimeters, controlled access.",
        heroSubhead:
            "Site fencing, security gates, and access systems for GTA commercial and industrial properties — installed around your operations, not in their way.",
        heroImage: "/images/commercial/commercial-fencing-gates-hero.d57e4168.webp",
        heroImageAlt: "Black powder-coated aluminum fence running along a grassed boulevard beside a busy multi-lane commercial road",
        intro:
            "Your perimeter is the first line of liability protection for any commercial or industrial site. We design, supply, and install chain-link, ornamental, and security fencing plus manual and automated gates across Brampton, Mississauga, Toronto, and the wider GTA. Every project ships with COIs and WSIB clearance, and we schedule around tenant and operational hours so access is never compromised.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Fully insured & WSIB-cleared",
                body: "Certificates of insurance and WSIB clearance issued before mobilization on every site.",
            },
            {
                icon: "Hammer",
                title: "Engineered for the load",
                body: "Posts, footings, and gauges spec'd to wind load, security rating, and gate weight — not guesswork.",
            },
            {
                icon: "Award",
                title: "Single-vendor turnkey",
                body: "Fence, automated gates, and access control delivered by one accountable team across your portfolio.",
            },
            {
                icon: "Clock",
                title: "Scheduled around operations",
                body: "Phased installs and off-hours work keep loading docks, parking, and tenants moving.",
            },
        ],
        materials: [
            {
                name: "Chain-Link & Security Fence",
                desc: "Galvanized or black PVC-coated chain-link with optional barbed or razor topping for yards and compounds.",
                image: "/images/commercial/commercial-fencing-gates-m1.d42982e6.webp",
                alt: "Galvanized chain-link fence topped with barbed wire enclosing a paved commercial storage yard",
            },
            {
                name: "Ornamental & Architectural Fence",
                desc: "Powder-coated steel and aluminum railing for storefronts, campuses, and street-facing frontages.",
                image: "/images/commercial/commercial-fencing-gates-m2.e8227cb8.webp",
                alt: "Black aluminum picket fence running along a sidewalk and grass boulevard on a street-facing property frontage, with brick entry piers further down the line",
            },
            {
                name: "Automated Gates & Operators",
                desc: "Slide and swing gate operators with safety loops, sensors, and fail-safe hardware.",
                image: "/images/commercial/commercial-fencing-gates-m3.de628141.webp",
                alt: "Black steel cantilever slide gate with an electric operator and safety sensors at a commercial site entrance",
            },
            {
                name: "Access Control Integration",
                desc: "Keypad, fob, intercom, and barrier-arm integration tied into your existing building systems.",
                image: "/images/commercial/commercial-fencing-gates-m4.cac9c743.webp",
                alt: "Stainless keypad and intercom access-control station on a pedestal beside a commercial gate",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We walk the perimeter, confirm property lines and utilities, and assess security, load, and access needs." },
            { num: "02", title: "Scope & Quote", body: "Line-item proposal with materials, gate specs, COIs, and a phasing plan that protects your operations." },
            { num: "03", title: "Scheduled Execution", body: "WSIB-cleared crews mobilize on agreed dates, working off-hours or in phases to keep the site running." },
            { num: "04", title: "Documentation & Sign-off", body: "Operational testing of gates and access control, as-built notes, warranty package, and walkthrough sign-off." },
        ],
        faqs: [
            {
                q: "Can you install commercial fencing without shutting down our site?",
                a: "Yes. The vast majority of our commercial installs are phased so that loading docks, parking, and tenant access stay open. We map out a sequence during the quote stage, can work evenings or weekends, and use temporary fencing where a section must come down before the new run goes up. Nothing about your day-to-day operation should stop because of the project.",
            },
            {
                q: "Do you provide a certificate of insurance and WSIB clearance?",
                a: "Always. Before any crew mobilizes we issue a certificate of insurance naming your property owner and management company as required, along with a current WSIB clearance certificate. This is standard on every commercial and industrial job and is part of our onboarding package for property managers and facility managers.",
            },
            {
                q: "Who is responsible for locates and property-line verification?",
                a: "We coordinate public utility locates as part of the project and recommend a current survey or stakeout for the property line before footings go in. For shared boundaries or tight industrial lots we will flag any survey gaps during the site assessment so there are no encroachment or liability surprises after installation.",
            },
            {
                q: "Can you service multiple properties across our GTA portfolio?",
                a: "Yes. We regularly work across Brampton, Mississauga, Toronto, and the surrounding GTA for owners and asset managers with multiple sites. A single point of contact, consistent specifications, and consolidated documentation make it straightforward to standardize fencing and gate work across an entire portfolio.",
            },
            {
                q: "Do you service and repair existing automated gates, not just new installs?",
                a: "We do. We repair and retrofit existing slide and swing gate operators, replace failed safety sensors and loops, and bring older gates up to current safety standards. If you have a gate that is failing intermittently or no longer safe, we can assess it and recommend repair versus replacement with clear costs for each.",
            },
        ],
    },

    "parking-lots-asphalt": {
        slug: "parking-lots-asphalt",
        category: "Commercial · Asphalt & Paving",
        name: "Parking Lots & Asphalt",
        heroTitle: "Pavement that protects your asset.",
        heroSubhead:
            "Asphalt paving, repair, sealing, and line painting that extend pavement life, reduce slip-and-fall liability, and keep your GTA property looking sharp.",
        heroImage: "/images/commercial/parking-lots-asphalt-hero.e7866227.webp",
        heroImageAlt: "Freshly placed asphalt beside a multi-unit residential parking area, with plate compactor and traffic cones on site.",
        intro:
            "A parking lot is one of the largest capital assets on a commercial property — and the first thing tenants, customers, and prospective buyers see. We deliver new asphalt construction, mill-and-pave overlays, crack and pothole repair, sealcoating, and line striping for plazas, industrial yards, and multi-site portfolios across Mississauga, Brampton, Toronto, and the GTA. Proactive maintenance defers six-figure reconstruction and limits trip-hazard exposure.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Liability-focused repairs",
                body: "We prioritize trip hazards, ponding, and ADA-relevant grades to reduce slip-and-fall exposure.",
            },
            {
                icon: "Clock",
                title: "Off-hours & phased paving",
                body: "Overnight and weekend pours plus half-lot phasing keep tenants and customers parking throughout.",
            },
            {
                icon: "Award",
                title: "Capital-planning support",
                body: "Condition assessments and budget tiers help you plan maintenance versus reconstruction with confidence.",
            },
            {
                icon: "Hammer",
                title: "Proper base & compaction",
                body: "Sound granular base and tested compaction so the surface carries traffic loads without premature failure.",
            },
        ],
        materials: [
            {
                name: "New Asphalt & Overlays",
                desc: "Full-depth construction or mill-and-pave overlays sized to traffic loads and drainage.",
                image: "/images/commercial/parking-lots-asphalt-m1.72c5469c.webp",
                alt: "Two freshly paved asphalt driveways meeting new concrete curb, caution tape still strung across the fresh surface.",
            },
            {
                name: "Crack & Pothole Repair",
                desc: "Hot-rubber crack sealing and infrared or saw-cut patching to stop water intrusion and damage.",
                image: "/images/commercial/parking-lots-asphalt-m2.00f658ee.webp",
                alt: "Worker raking hot asphalt into a saw-cut patch outlined in green marking paint on concrete pavement.",
            },
            {
                name: "Sealcoating",
                desc: "Commercial-grade sealer that restores surface, resists oxidation, and refreshes curb appeal.",
                image: "/images/commercial/parking-lots-asphalt-m3.b30bdbd0.webp",
                alt: "Freshly sealcoated asphalt meeting faded oxidised pavement at a crisp wet edge",
            },
            {
                name: "Line Painting & Markings",
                desc: "Stalls, fire routes, accessible spaces, and directional markings to code and site flow.",
                image: "/images/commercial/parking-lots-asphalt-m4.48d3a660.webp",
                alt: "Freshly painted yellow parking stall lines and directional chevron on asphalt alongside an industrial unit.",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We assess pavement condition, drainage, traffic patterns, and trip-hazard liability across the lot." },
            { num: "02", title: "Scope & Quote", body: "Tiered proposal — repair, maintenance, or reconstruction — with budgets, COIs, and a phasing plan." },
            { num: "03", title: "Scheduled Execution", body: "Off-hours or phased work with traffic control so the lot stays usable while crews pave and stripe." },
            { num: "04", title: "Documentation & Sign-off", body: "Cure-time guidance, as-completed markings layout, warranty package, and final walkthrough." },
        ],
        faqs: [
            {
                q: "How long does a commercial parking lot need to stay closed after paving?",
                a: "Fresh asphalt is typically ready for traffic within 24 to 72 hours depending on temperature, lift thickness, and weather. We almost always phase the work so only part of the lot is closed at a time, and we schedule pours overnight or on weekends where possible so tenants and customers always have somewhere to park. We provide exact cure-time guidance and signage for your site.",
            },
            {
                q: "Should we repair and sealcoat or fully reconstruct the lot?",
                a: "It depends on the condition of the base and the extent of cracking. If the granular base is sound and damage is surface-level, crack sealing, patching, and sealcoating can add many years of life at a fraction of reconstruction cost. If there is widespread alligator cracking, base failure, or chronic ponding, an overlay or full-depth rebuild is the better long-term value. Our condition assessment lays out both paths with budgets so you can plan capital accordingly.",
            },
            {
                q: "How often should sealcoating and line painting be redone?",
                a: "In the GTA climate, sealcoating every two to three years is a reasonable cycle to protect against freeze-thaw, UV oxidation, and fuel and oil damage. Line painting generally needs refreshing every one to two years, sooner in high-traffic lots. Building this into a maintenance plan keeps the lot safe, code-compliant, and presentable while deferring major spend.",
            },
            {
                q: "Do you handle fire routes, accessible parking, and code-compliant markings?",
                a: "Yes. We paint and stencil fire routes, accessible spaces and access aisles, loading zones, directional arrows, and stall layouts to municipal and site requirements. If your lot needs to be brought up to current standards for an inspection, tenant requirement, or property sale, we can lay it out correctly as part of the project.",
            },
            {
                q: "Can you maintain asphalt across several properties on one contract?",
                a: "Absolutely. For owners and property managers with multiple plazas, industrial yards, or sites across Brampton, Mississauga, Toronto, and the GTA, we offer portfolio-level asphalt maintenance with consistent specs, scheduled annual inspections, consolidated reporting, and a single point of contact — which simplifies budgeting and procurement.",
            },
        ],
    },

    "concrete-sidewalks": {
        slug: "concrete-sidewalks",
        category: "Commercial · Concrete",
        name: "Concrete & Sidewalks",
        heroTitle: "Concrete that carries the load.",
        heroSubhead:
            "Sidewalks, curbs, pads, stairs, and ramps poured to code and to load — reducing trip hazards and keeping your GTA property compliant and accessible.",
        heroImage: "/images/commercial/concrete-sidewalks-hero.63ddd17d.webp",
        heroImageAlt: "New broom-finished concrete sidewalk and curb along a commercial building frontage",
        intro:
            "Concrete failures create real liability: heaved sidewalks, cracked stairs, and settled pads are trip hazards and accessibility problems waiting to become claims. We pour and repair commercial walkways, curbs, equipment pads, loading aprons, stairs, and accessibility ramps across Toronto, Mississauga, Brampton, and the GTA — engineered for the loads they carry and finished for safe footing in every season.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Trip-hazard & accessibility focus",
                body: "We correct heaves, slopes, and non-compliant grades to reduce liability and meet accessibility needs.",
            },
            {
                icon: "Hammer",
                title: "Load-rated pads & aprons",
                body: "Mix design, rebar, and thickness spec'd for forklifts, trucks, and equipment — not just foot traffic.",
            },
            {
                icon: "Award",
                title: "Durable, finished correctly",
                body: "Proper curing, control joints, and broom or finish detailing so surfaces last and stay slip-resistant.",
            },
            {
                icon: "Clock",
                title: "Minimal-downtime pours",
                body: "Phased sections and rapid-set options keep entrances, docks, and walkways usable.",
            },
        ],
        materials: [
            {
                name: "Sidewalks & Walkways",
                desc: "New and replacement walkways with proper jointing, slope, and slip-resistant finish.",
                image: "/images/commercial/concrete-sidewalks-m1.ad416ba8.webp",
                alt: "Completed stepped concrete walkway beside a building with tooled joints, broom finish and gravel drainage strip.",
            },
            {
                name: "Curbs & Drainage",
                desc: "Cast-in-place and pre-cast curbs, gutters, and drainage details that direct water away.",
                image: "/images/commercial/concrete-sidewalks-m2.84daaaf7.webp",
                alt: "Cast-in-place concrete curb and gutter directing water to a catch basin in a commercial lot",
            },
            {
                name: "Equipment Pads & Aprons",
                desc: "Load-rated pads and loading aprons reinforced for heavy and repeated traffic.",
                image: "/images/commercial/concrete-sidewalks-m3.1f8e0bac.webp",
                alt: "Reinforced concrete loading apron with saw-cut control joints at a commercial overhead door",
            },
            {
                name: "Stairs & Accessibility Ramps",
                desc: "Code-compliant stairs, landings, and ramps with proper rise, run, and handrail provisions.",
                image: "/images/commercial/concrete-sidewalks-m4.835291f7.webp",
                alt: "Concrete entrance stairs beside an accessibility ramp with steel handrails and tactile strip",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We assess existing concrete, grades, drainage, load requirements, and accessibility or trip-hazard issues." },
            { num: "02", title: "Scope & Quote", body: "Detailed proposal with mix design, reinforcement, finish, COIs, and a low-disruption phasing plan." },
            { num: "03", title: "Scheduled Execution", body: "Crews form, pour, and finish in phases so entrances, docks, and walkways stay accessible." },
            { num: "04", title: "Documentation & Sign-off", body: "Cure-protection plan, as-built details, warranty package, and walkthrough before reopening." },
        ],
        faqs: [
            {
                q: "How quickly can foot and vehicle traffic return to new concrete?",
                a: "Foot traffic is generally fine after 24 to 48 hours, while full vehicle and equipment loading typically needs about 7 days to reach adequate strength, with full design strength at around 28 days. Where downtime is critical — a main entrance or active loading apron — we can use high-early-strength or rapid-set mixes to shorten the closure. We give you specific reopening times for each pour.",
            },
            {
                q: "Can damaged concrete be repaired, or does the whole slab need replacing?",
                a: "It depends on the cause and extent. Surface spalling and isolated cracks can often be repaired or resurfaced. But heaving from base or drainage issues, structural cracking, or settled slabs usually require removal and replacement of the affected sections, plus correcting the underlying cause so the problem does not return. We assess this honestly during the site visit and quote the right fix rather than a band-aid.",
            },
            {
                q: "Do you handle accessibility ramps and code-compliant grades?",
                a: "Yes. We build and correct accessibility ramps, landings, and walkway grades to meet code and accessibility requirements, including proper slope, level landings, and handrail provisions. If your property needs to address an accessibility deficiency or an inspection finding, we can scope and execute the corrective work.",
            },
            {
                q: "Why does commercial concrete in the GTA crack and heave?",
                a: "The main culprits are freeze-thaw cycles, inadequate or poorly draining base, missing or improperly spaced control joints, and overloading beyond the slab's design. Our pours address these directly: proper granular base and compaction, correct reinforcement, control and expansion joints, drainage detailing, and an air-entrained mix suited to the Ontario climate. That is what separates concrete that lasts from concrete that fails in a few winters.",
            },
            {
                q: "Are you insured and WSIB-compliant for work on our property?",
                a: "Yes. We provide a certificate of insurance naming the required parties and a current WSIB clearance certificate before mobilizing. For property and facility managers this documentation is included as a standard part of our onboarding, so the paperwork is handled before crews ever step on site.",
            },
        ],
    },

    "interlocking-hardscape": {
        slug: "interlocking-hardscape",
        category: "Commercial · Hardscape",
        name: "Interlocking & Hardscape",
        heroTitle: "Hardscape that elevates the property.",
        heroSubhead:
            "Interlocking pavers, retaining walls, plazas, and entrances that boost curb appeal and tenant experience while standing up to GTA freeze-thaw.",
        heroImage: "/images/commercial/interlocking-hardscape-hero.a1c4360e.webp",
        heroImageAlt: "Finished interlocking paver patio and walkway with dark border edging in a fenced backyard",
        intro:
            "First impressions drive leasing, retail traffic, and asset value — and hardscape is where they are made. We design and build commercial interlocking, segmental retaining walls, courtyards, walkways, and entrance plazas across Mississauga, Brampton, Toronto, and the GTA. Built on engineered bases with the right edge restraint and drainage, our hardscape resists heaving and settlement so it looks intentional for years, not seasons.",
        valueBullets: [
            {
                icon: "Award",
                title: "Curb appeal that leases space",
                body: "Polished entrances, plazas, and walkways that lift tenant experience and asset perception.",
            },
            {
                icon: "Hammer",
                title: "Engineered bases & walls",
                body: "Proper sub-base, geogrid, and drainage so pavers and retaining walls don't heave or bulge.",
            },
            {
                icon: "ShieldCheck",
                title: "Safe, even surfaces",
                body: "Level, well-drained walkways reduce ponding, ice build-up, and trip-hazard liability.",
            },
            {
                icon: "Clock",
                title: "Phased around tenants",
                body: "We sequence entrances and walkways so access and storefront visibility stay open.",
            },
        ],
        materials: [
            {
                name: "Commercial Pavers",
                desc: "Heavy-duty interlocking pavers for plazas, walkways, and vehicular-rated areas.",
                image: "/images/commercial/interlocking-hardscape-m1.55580582.webp",
                alt: "Backyard patio of large-format grey paver slabs in a mixed modular pattern with a charcoal border course",
            },
            {
                name: "Segmental Retaining Walls",
                desc: "Engineered SRW systems with geogrid reinforcement and proper drainage for grade changes.",
                image: "/images/commercial/interlocking-hardscape-m2.e5821075.webp",
                alt: "Engineered segmental retaining wall with capped top course beside a commercial walkway",
            },
            {
                name: "Entrances & Plazas",
                desc: "Feature entrances, courtyards, and gathering spaces designed for traffic and impression.",
                image: "/images/commercial/interlocking-hardscape-m3.5331356a.webp",
                alt: "Paver entrance walkway with charcoal curb edging leading to precast concrete steps and a black aluminum railing",
            },
            {
                name: "Steps, Edging & Drainage",
                desc: "Paver steps, edge restraint, and integrated drainage that keep the installation stable and dry.",
                image: "/images/commercial/interlocking-hardscape-m4.547e4ad2.webp",
                alt: "Charcoal paver steps with wide treads descending onto a large-format slab patio",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We assess grades, drainage, traffic loads, and the design intent for the space and its approaches." },
            { num: "02", title: "Scope & Quote", body: "Design direction, materials, base build-up, COIs, and a phasing plan that protects tenant access." },
            { num: "03", title: "Scheduled Execution", body: "Excavation, engineered base, laying, and compaction sequenced to keep entrances open and clean." },
            { num: "04", title: "Documentation & Sign-off", body: "Maintenance guidance, as-built layout, warranty package, and a final walkthrough with management." },
        ],
        faqs: [
            {
                q: "Will interlocking pavers heave or settle in the GTA climate?",
                a: "Not when they are built correctly. Heaving and settlement almost always trace back to an inadequate base, poor drainage, or missing edge restraint. We excavate to the proper depth, install and compact a quality granular base, set bedding sand correctly, use solid edge restraint, and detail drainage so water moves away from the assembly. Built this way, commercial interlocking handles freeze-thaw cycles for many years.",
            },
            {
                q: "Are pavers a good choice for vehicular and high-traffic commercial areas?",
                a: "Yes, with the right product and base. We use thicker, higher-strength pavers and a reinforced base for drive lanes, fire routes, and loading areas. A key advantage over poured surfaces is serviceability — if a section ever needs access for utilities or settles, individual pavers can be lifted and reset rather than saw-cutting and re-pouring concrete, which lowers lifetime cost.",
            },
            {
                q: "Do segmental retaining walls need engineering?",
                a: "Walls above a certain height, or any wall supporting a surcharge such as a parking area or driveway, generally require engineered design and may need a permit. We identify this during the assessment, involve a structural engineer where required, and build with geogrid reinforcement and proper drainage so the wall performs and passes inspection. Lower, non-load-bearing walls can often be built without engineering.",
            },
            {
                q: "How do you minimize disruption to tenants and customers during a hardscape build?",
                a: "We phase the work so a building always has a usable entrance and walkway, stage materials to keep sightlines and access clear, and clean the site daily. For retail and office properties we can sequence storefront entrances one at a time and work off-hours on the most disruptive stages. The goal is an upgraded property without interrupting business.",
            },
            {
                q: "What ongoing maintenance does commercial hardscape need?",
                a: "Relatively little. Periodic polymeric sand replenishment in the joints, occasional cleaning, and re-leveling of any minor settlement keep the installation looking and performing well. We provide a simple maintenance plan at handover and can fold periodic hardscape upkeep into a broader grounds maintenance agreement across your GTA portfolio.",
            },
        ],
    },

    "commercial-masonry-restoration": {
        slug: "commercial-masonry-restoration",
        category: "Commercial · Masonry & Restoration",
        name: "Commercial Masonry & Restoration",
        heroTitle: "Restore the envelope, protect the building.",
        heroSubhead:
            "Brick, block, and stone repair, repointing, and restoration that protect your building envelope, address safety orders, and preserve asset value.",
        heroImage: "/images/commercial/commercial-masonry-restoration-hero.2f6021f3.webp",
        heroImageAlt: "Beige brick commercial wall with rebuilt loading dock opening, new brick jambs and resurfaced concrete dock face",
        intro:
            "Deteriorating masonry is more than cosmetic — failing mortar joints, spalling brick, and unstable parapets let water into the envelope and can trigger safety orders. We deliver commercial repointing, brick and stone replacement, lintel repair, parapet and chimney rebuilds, and waterproofing across Toronto, Mississauga, Brampton, and the GTA. Restoring the envelope protects interiors, extends building life, and keeps your property compliant.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Envelope & water protection",
                body: "Repointing, sealing, and flashing repairs stop water intrusion before it damages interiors and structure.",
            },
            {
                icon: "Hammer",
                title: "Matched materials & detailing",
                body: "Mortar color, profile, and brick matched so repairs blend into the building, not stand out.",
            },
            {
                icon: "Award",
                title: "Restoration done right",
                body: "Proper access, dust control, and craftsmanship that preserve heritage and modern facades alike.",
            },
            {
                icon: "Clock",
                title: "Compliance-driven scheduling",
                body: "We can prioritize safety-order and parapet work and stage around occupied buildings.",
            },
        ],
        materials: [
            {
                name: "Repointing & Tuckpointing",
                desc: "Removing failed mortar and repointing with matched color and profile to seal the wall.",
                image: "/images/commercial/commercial-masonry-restoration-m1.2a2d2769.webp",
                alt: "Painted brick wall base with grey replacement bricks set in fresh mortar beside an overhead door",
            },
            {
                name: "Brick & Stone Replacement",
                desc: "Cutting out spalled or damaged units and replacing with matched brick and stone.",
                image: "/images/commercial/commercial-masonry-restoration-m2.6b524d94.webp",
                alt: "New grey bricks toothed into the corner of a painted brick wall with fresh mortar",
            },
            {
                name: "Parapets, Lintels & Chimneys",
                desc: "Rebuilding unstable parapets, replacing rusted lintels, and addressing safety-order items.",
                image: "/images/commercial/commercial-masonry-restoration-m3.0160a1ca.webp",
                alt: "Rusted steel lintel and spalled masonry around a commercial overhead door opening awaiting replacement",
            },
            {
                name: "Waterproofing & Sealing",
                desc: "Caulking, flashing, and breathable sealers that keep water out without trapping moisture.",
                image: "/images/commercial/commercial-masonry-restoration-m4.ffda4a48.webp",
                alt: "Freshly parged and caulked base of a precast concrete building wall along a sidewalk",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We inspect the facade, identify water entry points and safety risks, and assess access requirements." },
            { num: "02", title: "Scope & Quote", body: "Prioritized proposal with materials, access method, COIs, and scheduling for occupied buildings." },
            { num: "03", title: "Scheduled Execution", body: "Crews set up safe access, control dust and debris, and complete repairs matched to the existing facade." },
            { num: "04", title: "Documentation & Sign-off", body: "Before-and-after records, warranty package, and a walkthrough confirming the envelope is sound." },
        ],
        faqs: [
            {
                q: "How do we know if our building needs repointing or full restoration?",
                a: "Telltale signs include crumbling or recessed mortar joints, cracked or spalling brick, efflorescence, water stains on interior walls, and loose units. Where the issue is limited to mortar, repointing seals the wall. Where brick, stone, lintels, or parapets have failed, those elements need replacement or rebuilding. We provide a condition assessment that distinguishes routine maintenance from urgent structural work so you can prioritize.",
            },
            {
                q: "Can you address a property-standards or safety order on a masonry facade or parapet?",
                a: "Yes. Loose masonry, unstable parapets, and falling-debris risks are common subjects of municipal orders. We can inspect the cited deficiency, scope the corrective work, prioritize it, and provide the documentation you need to demonstrate compliance. For urgent overhead hazards we can also arrange protective measures while the permanent repair is scheduled.",
            },
            {
                q: "Will the repair match the existing brick and mortar?",
                a: "Matching is a core part of the work. We match mortar color, joint profile, and texture, and source replacement brick and stone as close as possible to the original. On older and heritage buildings we are careful to use compatible, breathable mortar rather than a hard modern mix that can damage soft historic brick. The goal is a repair that disappears into the facade.",
            },
            {
                q: "Do you work on occupied commercial buildings without disrupting tenants?",
                a: "Yes. We set up safe, contained access — swing stages, scaffolding, or boom lifts as appropriate — protect entrances and walkways from debris, and control dust. Where work is over an entrance or busy area we can schedule it for off-hours or phase it so tenants and the public stay safe and access stays open throughout the project.",
            },
            {
                q: "What causes masonry deterioration in the GTA, and how do you prevent recurrence?",
                a: "Freeze-thaw cycling, water infiltration through failed joints and flashing, rusting steel lintels that expand and crack masonry, and incompatible past repairs are the usual causes. We do not just patch the symptom — we correct water entry with proper repointing, flashing, and sealing, replace failed structural elements, and use compatible materials so the same failure does not return. Pairing the repair with a maintenance plan further extends envelope life.",
            },
        ],
    },

    "landscaping-grounds": {
        slug: "landscaping-grounds",
        category: "Commercial · Grounds",
        name: "Landscaping & Grounds",
        heroTitle: "Grounds that work as hard as you do.",
        heroSubhead:
            "Year-round commercial landscaping and grounds maintenance that keeps your GTA property presentable, safe, and lease-ready — on one accountable contract.",
        heroImage: "/images/commercial/landscaping-grounds-hero.4abe3ff8.webp",
        heroImageAlt: "Well-maintained commercial grounds with cut lawn, edged mulch beds and a clean walkway",
        intro:
            "Well-kept grounds signal a well-managed property — and neglected ones cost tenants, traffic, and value. We provide commercial landscaping and full-season grounds maintenance for plazas, office parks, multi-residential, and industrial sites across Brampton, Mississauga, Toronto, and the GTA, including turf care, plantings, bed maintenance, and litter and debris management. One vendor, one schedule, one point of contact for the entire property or portfolio.",
        valueBullets: [
            {
                icon: "Award",
                title: "Lease-ready curb appeal",
                body: "Healthy turf, sharp beds, and clean grounds that strengthen tenant retention and asset value.",
            },
            {
                icon: "Clock",
                title: "Reliable scheduled service",
                body: "Consistent maintenance routes and reporting so the property always looks tended, not reactive.",
            },
            {
                icon: "Award",
                title: "Single-vendor convenience",
                body: "Landscaping, grounds, and seasonal services consolidated under one contract and one contact.",
            },
            {
                icon: "ShieldCheck",
                title: "Insured & WSIB-cleared crews",
                body: "COIs and WSIB clearance on file, with trained crews on every visit to your site.",
            },
        ],
        materials: [
            {
                name: "Turf & Lawn Care",
                desc: "Mowing, edging, fertilization, and overseeding programs to keep turf healthy and presentable.",
                image: "/images/commercial/landscaping-grounds-m1.dba97354.webp",
                alt: "Freshly laid sod covering a large backyard in even green, running up to a concrete walkway, with a deck and garden shed behind",
            },
            {
                name: "Plantings & Garden Beds",
                desc: "Seasonal plantings, shrubs, mulching, and bed maintenance that elevate entrances and frontages.",
                image: "/images/commercial/landscaping-grounds-m2.9933ef67.webp",
                alt: "Newly planted cedar shrubs in a freshly mulched bed edging a wooden fence and lawn",
            },
            {
                name: "Pruning & Tree Care",
                desc: "Shrub and ornamental pruning plus coordinated tree work to keep sightlines and safety clear.",
                image: "/images/commercial/landscaping-grounds-m3.f0ff2598.webp",
                alt: "Freshly pruned shrubs and a shaped young tree in a mulched bed at a commercial frontage",
            },
            {
                name: "Grounds & Litter Management",
                desc: "Litter pickup, blowing, and debris removal that keep walkways, lots, and entrances clean.",
                image: "/images/commercial/landscaping-grounds-m4.c654ca93.webp",
                alt: "Spotless commercial entrance walkway after grounds service with a leaf blower set aside",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We walk the grounds, inventory turf, beds, and trees, and identify presentation and safety priorities." },
            { num: "02", title: "Scope & Quote", body: "Season-long maintenance plan with service frequencies, COIs, and transparent line-item pricing." },
            { num: "03", title: "Scheduled Execution", body: "Trained crews service the property on a consistent schedule, with quality checks each visit." },
            { num: "04", title: "Documentation & Sign-off", body: "Service reporting, seasonal reviews, and an open line for management to adjust scope as needed." },
        ],
        faqs: [
            {
                q: "Can we bundle landscaping with snow removal and other services on one contract?",
                a: "Yes, and most of our commercial clients do. Consolidating grounds maintenance with snow and ice management — and other exterior services — under one contract gives you a single point of contact, one set of insurance and WSIB documentation, coordinated scheduling, and simpler invoicing. It removes the gaps that happen when multiple vendors each handle a slice of the property.",
            },
            {
                q: "How do you keep service consistent across a multi-site portfolio?",
                a: "We assign defined service routes and frequencies, document a scope of work for each property, and perform quality checks on every visit. For owners and managers with sites across Brampton, Mississauga, Toronto, and the GTA we provide consolidated reporting and a single account contact, so the standard is the same whether it is one plaza or a dozen.",
            },
            {
                q: "Are your crews insured and WSIB-compliant?",
                a: "Yes. We carry commercial liability insurance and maintain WSIB coverage, and we provide certificates of insurance and clearance certificates as part of onboarding. This is important on commercial grounds where the public and tenants are present, and it is standard documentation we keep on file for your property management records.",
            },
            {
                q: "What is included in a typical commercial grounds maintenance program?",
                a: "A typical program covers regular mowing, edging, and trimming; fertilization and turf health; spring and fall cleanups; bed maintenance and mulching; seasonal plantings; shrub pruning; and ongoing litter and debris management. We tailor the frequency and inclusions to your property type and budget, and the scope is laid out clearly in the agreement so there are no surprises.",
            },
            {
                q: "Can you take over mid-season from another contractor?",
                a: "Yes. We regularly take over properties mid-season when an owner or manager is not getting the service they expected. We start with a site assessment to catch up anything that has been neglected, then move the property onto our regular schedule. The transition is straightforward and we handle the documentation needed to get started quickly.",
            },
        ],
    },

    "snow-ice-management": {
        slug: "snow-ice-management",
        category: "Commercial · Seasonal",
        name: "Snow & Ice Management",
        heroTitle: "Open lots, lower liability, every storm.",
        heroSubhead:
            "Seasonal commercial snow plowing, salting, and ice management that keeps your GTA property safe, accessible, and protected from slip-and-fall claims.",
        heroImage: "/images/commercial/snow-ice-management-hero.7a520d60.webp",
        heroImageAlt: "Plowed commercial plaza parking lot at dawn with snow banked along the lot perimeter",
        intro:
            "Snow and ice management is the most liability-sensitive seasonal service on any commercial property — and the GTA winter does not wait. We provide seasonal contracts for plowing, salting, sidewalk clearing, and ice control across Brampton, Mississauga, Toronto, and the surrounding region, with storm monitoring and detailed service records. The result is open lots, safe walkways, and documentation that protects you when a slip-and-fall claim is filed.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Slip-and-fall liability defense",
                body: "Time-stamped service logs and salting records that support your defense against winter claims.",
            },
            {
                icon: "Clock",
                title: "Storm-triggered response",
                body: "Seasonal contracts with weather monitoring so crews mobilize before lots and walkways become unsafe.",
            },
            {
                icon: "Award",
                title: "Single-vendor for the season",
                body: "Plowing, salting, and sidewalk clearing across your portfolio under one accountable contract.",
            },
            {
                icon: "Hammer",
                title: "Property-protective practices",
                body: "Marked plow routes and curb protection that clear snow without tearing up your asphalt and landscaping.",
            },
        ],
        materials: [
            {
                name: "Snow Plowing & Clearing",
                desc: "Lot plowing, stacking, and hauling to keep parking, drive lanes, and fire routes open.",
                image: "/images/commercial/snow-ice-management-m1.07eee43c.webp",
                alt: "Pickup truck with a front plow blade clearing snow across a commercial parking lot",
            },
            {
                name: "Salting & De-Icing",
                desc: "Granular and liquid de-icing applications on lots and walkways for traction and ice control.",
                image: "/images/commercial/snow-ice-management-m2.9ab7d140.webp",
                alt: "Granular ice-melt salt spread across a wet commercial parking surface and walkway",
            },
            {
                name: "Sidewalk & Entrance Clearing",
                desc: "Manual and machine clearing of walkways, entrances, and accessible routes for safe footing.",
                image: "/images/commercial/snow-ice-management-m3.b5078753.webp",
                alt: "Shovelled and salted concrete walkway leading to a commercial building entrance",
            },
            {
                name: "Service Logging & Reporting",
                desc: "Time-stamped records of every plow and salt visit to support liability defense and billing.",
                image: "/images/commercial/snow-ice-management-m4.8bb450ff.webp",
                alt: "Gloved hand logging a service visit on a rugged tablet in a truck cab during snowfall",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "Before the season we map plow routes, snow-storage areas, priority walkways, and obstacles to mark." },
            { num: "02", title: "Scope & Quote", body: "Seasonal contract with trigger depths, salting protocol, COIs, and clear per-event or seasonal pricing." },
            { num: "03", title: "Scheduled Execution", body: "Weather-triggered crews plow, salt, and clear walkways through each storm to keep the site open." },
            { num: "04", title: "Documentation & Sign-off", body: "Time-stamped service logs after every event and a season-end review for liability and budgeting." },
        ],
        faqs: [
            {
                q: "How does snow and ice service protect us from slip-and-fall claims?",
                a: "The single most important factor in defending a winter slip-and-fall claim is documentation. We keep time-stamped records of every plowing and salting visit, including conditions and the work performed, so you can demonstrate that the property was serviced reasonably and on time. Combined with consistent salting of walkways and entrances, this record is your strongest protection when a claim is filed.",
            },
            {
                q: "Do you offer seasonal contracts or per-event billing?",
                a: "Both. A seasonal (flat-rate) contract gives you budget certainty regardless of how many storms hit, which most property managers prefer. Per-event billing charges by the visit and may suit lower-traffic sites. We will recommend the structure that fits your property and risk tolerance, and define trigger depths and salting protocols clearly in the agreement either way.",
            },
            {
                q: "When do crews mobilize during a storm?",
                a: "Mobilization is governed by the trigger thresholds in your contract — typically a set snow accumulation for plowing and a temperature and condition threshold for salting. We monitor weather closely and aim to service priority areas before they become hazardous, with continued passes during prolonged storms and a final cleanup once snowfall ends. High-priority sites and accessible routes get attention first.",
            },
            {
                q: "Will plowing damage our asphalt, curbs, or landscaping?",
                a: "Not with proper practices. Before the season we mark curbs, islands, hydrants, and sensitive landscaping, and we map plow routes and snow-storage areas to avoid them. Our operators use appropriate blade and equipment settings to clear effectively without gouging asphalt or catching curbs. Protecting the asset is part of the service, not an afterthought.",
            },
            {
                q: "Can one vendor handle snow across all our GTA sites?",
                a: "Yes. We provide portfolio-wide seasonal snow and ice management across Brampton, Mississauga, Toronto, and the surrounding GTA, with consistent protocols, consolidated documentation, and a single point of contact. Pairing winter service with your landscaping and grounds contract gives you year-round coverage of the property from one accountable team.",
            },
        ],
    },

    "exterior-renovations-buildouts": {
        slug: "exterior-renovations-buildouts",
        category: "Commercial · Exterior Build-Outs",
        name: "Exterior Renovations & Build-Outs",
        heroTitle: "Reposition the exterior, lift the asset.",
        heroSubhead:
            "Exterior renovations and build-outs — facades, entrances, and site improvements — that modernize GTA commercial properties and support tenant attraction.",
        heroImage: "/images/commercial/exterior-renovations-buildouts-hero.00d7f0bf.webp",
        heroImageAlt: "Wide view of an industrial block wall with repainted steel doors, new louvres and repaired base.",
        intro:
            "Exterior renovations are one of the highest-leverage ways to reposition a commercial asset — improving curb appeal, tenant attraction, and rents without touching the core. We manage exterior build-outs across Mississauga, Brampton, Toronto, and the GTA: facade refreshes, new and upgraded entrances, storefront work, cladding, and integrated site improvements. As a single accountable contractor we coordinate the trades, the schedule, and the documentation so the project lands on time and around your tenants.",
        valueBullets: [
            {
                icon: "Award",
                title: "Repositioning that pays back",
                body: "Facade and entrance upgrades that improve curb appeal, tenant attraction, and achievable rents.",
            },
            {
                icon: "Hammer",
                title: "Single accountable contractor",
                body: "We coordinate trades, materials, and inspections so you manage one team, not five subcontractors.",
            },
            {
                icon: "Clock",
                title: "Built around occupancy",
                body: "Phased schedules and off-hours work keep tenants operating and entrances open during the build.",
            },
            {
                icon: "ShieldCheck",
                title: "Permitted & compliant",
                body: "We manage permits, COIs, and WSIB so the work is documented, inspected, and signed off cleanly.",
            },
        ],
        materials: [
            {
                name: "Facade Refresh & Cladding",
                desc: "Re-cladding, panel systems, and facade updates that modernize the building exterior.",
                image: "/images/commercial/exterior-renovations-buildouts-m1.e05a4c49.webp",
                alt: "Concrete block wall with a repaired and parged foundation base running alongside a freshly striped parking area.",
            },
            {
                name: "Entrances & Storefronts",
                desc: "New and upgraded entrances, vestibules, and storefronts that strengthen first impressions.",
                image: "/images/commercial/exterior-renovations-buildouts-m2.568c9707.webp",
                alt: "Completed commercial storefront entrance with glazed vestibule and lit canopy soffit",
            },
            {
                name: "Site Improvements",
                desc: "Coordinated walkways, lighting, signage bases, and exterior features tied into the renovation.",
                image: "/images/commercial/exterior-renovations-buildouts-m3.20fb4f7b.webp",
                alt: "Freshly poured concrete walkway running along a row of commercial unit entrances in low sunlight.",
            },
            {
                name: "Trade Coordination & Permits",
                desc: "Managing trades, scheduling, permits, and inspections so the build-out runs as one project.",
                image: "/images/commercial/exterior-renovations-buildouts-m4.0abe4863.webp",
                alt: "Hard hat, drawings and a checklist on a work truck tailgate at a commercial build-out",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We review the existing exterior, repositioning goals, occupancy constraints, and permitting requirements." },
            { num: "02", title: "Scope & Quote", body: "Defined scope, trade plan, schedule, permits, COIs, and pricing aligned to your repositioning budget." },
            { num: "03", title: "Scheduled Execution", body: "We coordinate the trades and inspections in phases, working around tenants to keep the property open." },
            { num: "04", title: "Documentation & Sign-off", body: "Closeout package with permits, warranties, as-builts, and a walkthrough confirming the scope is complete." },
        ],
        faqs: [
            {
                q: "Can exterior renovations be done while the building stays occupied?",
                a: "Yes. We build the schedule around occupancy from the start — phasing work so entrances stay open, staging materials to keep access and sightlines clear, and performing the most disruptive stages off-hours where needed. Tenant communication and a clean, contained worksite are part of how we run exterior build-outs so business continues throughout the project.",
            },
            {
                q: "Do you handle permits and trade coordination, or do we manage that?",
                a: "We handle it. As the single accountable contractor we manage the permit applications and inspections, coordinate all the trades, and own the schedule. You deal with one team and one point of contact rather than chasing multiple subcontractors. That coordination is a large part of the value — it keeps the project on time, on budget, and properly documented.",
            },
            {
                q: "What kind of return can an exterior renovation deliver?",
                a: "Exterior repositioning is typically one of the more cost-effective ways to lift a commercial asset because it improves curb appeal and tenant attraction without disturbing the building core. Refreshed facades and entrances can support stronger leasing, tenant retention, and rents, and improve how the property shows to prospective buyers. We scope the work to align with your repositioning goals and budget so the spend is targeted.",
            },
            {
                q: "How do you keep an exterior build-out on schedule and on budget?",
                a: "We define the scope and trade sequence in detail before starting, lock in materials early to avoid lead-time surprises, and coordinate inspections so they do not stall the work. Because we manage the trades directly, we can resolve conflicts on site rather than waiting on multiple vendors. You get regular progress updates and clear change-order handling if scope shifts.",
            },
            {
                q: "Can you work across multiple properties for a repositioning program?",
                a: "Yes. For owners and developers rolling out a consistent look across several GTA properties, we can standardize specifications, materials, and documentation across the portfolio while phasing each site around its tenants. A single contractor across the program keeps the brand and quality consistent and simplifies procurement and reporting.",
            },
        ],
    },
};

export const COMMERCIAL_SERVICES_LIST = Object.values(COMMERCIAL_SERVICES_DATA);
